import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';

import { cn } from '@app/libs/utils';

import Checkbox from './Checkbox';

export interface DropdownOption {
  icon?: React.ReactNode;
  color?: string;
  className?: string;
  value: string | undefined;
  label: string | React.ReactNode;
  onClick?: () => void;
}

interface DropdownProps {
  options?: DropdownOption[];
  className?: string;
  title?: string;
  align?: 'left' | 'right';
  initialOpen?: boolean;
  dropdownClassName?: string;
  triggerClassName?: string;
  optionClassName?: string;
  onOptionClick?: (option: DropdownOption) => void;
  children: React.ReactNode;
  disabled?: boolean;
  loadOptions?: (query: string) => Promise<DropdownOption[]>;
  searchable?: boolean;
  debounceMs?: number;
  placeholder?: string;
  noOptionsText?: string;
  value?: string | string[];
  onChange?: (value?: string | string[]) => void;
  otherParent?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  description?: string | React.ReactNode;
  multiple?: boolean;
  allOption?: string;
  htmlFunc?: (...args: any[]) => React.ReactNode | string;
  valueFunc?: (...args: any[]) => string;
}

const Dropdown: React.FC<DropdownProps> = ({
  otherParent,
  options,
  title,
  className = '',
  dropdownClassName = '',
  align = 'right',
  initialOpen = false,
  onOptionClick,
  children,
  disabled = false,
  loadOptions,
  triggerClassName = '',
  optionClassName = '',
  searchable = false,
  debounceMs = 300,
  placeholder = 'Tìm kiếm...',
  noOptionsText = 'Không có dữ liệu',
  value,
  onChange,
  onOpen,
  onClose,
  description,
  multiple = false,
  allOption,
  htmlFunc,
  valueFunc,
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [openUp, setOpenUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [asyncOptions, setAsyncOptions] = useState<DropdownOption[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cacheRef = useRef<Map<string, DropdownOption[]>>(new Map());

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as any;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
        onClose?.();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        onClose?.();
      }
    };

    // Use capture phase to ensure we catch the event before other handlers
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const recomputePlacement = () => {
      if (!dropdownRef.current) return;
      if (!menuRef.current) return;

      const triggerRect = dropdownRef.current.getBoundingClientRect();
      const parentRect = dropdownRef.current.parentElement?.getBoundingClientRect() || {
        top: 0,
        left: 0,
      };
      const menuEl = menuRef.current;
      const menuHeight = menuEl ? menuEl.offsetHeight : 0;
      const menuWidth = menuEl ? menuEl.offsetWidth : 0;
      const offsetLeft = menuEl ? triggerRect.left + triggerRect.width - menuEl.offsetWidth : 0;

      // Tính toán và set position left/right
      if (align === 'right') {
        if (offsetLeft <= 10) {
          menuEl.style.left = `-${triggerRect.left - 24}px`;
          menuEl.style.right = 'auto';
        } else {
          menuEl.style.left = '';
          menuEl.style.right = '';
        }
      } else {
        if (offsetLeft + menuWidth >= window.innerWidth - 10) {
          menuEl.style.left = `calc(100vw - ${menuWidth}px - 10px)`;
          menuEl.style.right = 'auto';
        } else {
          menuEl.style.left = '';
          menuEl.style.right = '';
        }
      }

      // Tính toán và set position top/bottom
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const shouldOpenUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;
      setOpenUp(shouldOpenUp);

      if (shouldOpenUp) {
        // Mở lên trên: set bottom position (relative to parent)
        // bottom tính từ bottom của parent đến bottom của trigger
        menuEl.style.bottom = `-4px`;
        menuEl.style.top = 'auto';
      } else {
        // Mở xuống dưới: set top position (relative to parent)
        // top tính từ top của parent đến bottom của trigger
        const triggerBottomRelative = triggerRect.height;
        const topPosition = triggerBottomRelative + 4; // 4px margin
        menuEl.style.top = `${topPosition}px`;
        menuEl.style.bottom = 'auto';
      }
    };

    // Measure immediately on open, before paint
    recomputePlacement();
    window.addEventListener('resize', recomputePlacement);
    window.addEventListener('scroll', recomputePlacement, true);
    return () => {
      window.removeEventListener('resize', recomputePlacement);
      window.removeEventListener('scroll', recomputePlacement, true);
    };
  }, [isOpen]);

  // Manage async loading when open/search changes
  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    } else {
      onClose?.();
    }
    if (!isOpen || !loadOptions) return;

    // Use cache if query unchanged
    const cached = cacheRef.current.get(query);
    if (cached) {
      setAsyncOptions(cached);
      return; // Skip re-fetch
    }

    let aborted = false;
    const run = async () => {
      try {
        setLoading(true);
        const items = await loadOptions(query);
        if (!aborted) {
          setAsyncOptions(items);
          cacheRef.current.set(query, items);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    run();
    return () => {
      aborted = true;
    };
  }, [isOpen, loadOptions, query]);

  // Debounce query updates (minimal: rely on native delay via setTimeout)
  const debouncedSetQuery = useMemo(() => {
    let t: any;
    return (val: string) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => setQuery(val), debounceMs);
    };
  }, [debounceMs]);

  const handleOptionClick = (option: DropdownOption) => {
    if (multiple) {
      const newValue = [...((value as string[]) || [])];
      if (option.value && newValue.includes(option.value)) {
        newValue.splice(newValue.indexOf(option.value), 1);
      } else {
        if (option.value) {
          newValue.push(option.value);
        }
      }

      onChange?.(newValue);
    } else {
      onChange?.(option.value);
      setIsOpen(false);
    }

    option?.onClick?.();
    onOptionClick?.(option);
  };

  const handleClear = () => {
    onChange?.(multiple ? [] : undefined);
    if (!multiple) {
      setIsOpen(false);
    }
  };

  return (
    <div className={cn(otherParent ? 'static' : 'relative', className)} ref={dropdownRef}>
      <div
        className={cn(
          'cursor-pointer group flex justify-between items-center',
          triggerClassName,
          isOpen && 'open',
          disabled && 'disabled'
        )}
        onClick={() => setIsOpen(!disabled && !isOpen)}
      >
        {children}
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className={cn(
            'absolute bg-white border border-gray-200 rounded-lg shadow-lg z-50 transition-all duration-300',
            otherParent ? 'max-w-full' : 'max-w-none',
            dropdownClassName,
            !dropdownClassName.includes('z-') && 'z-50',
            !dropdownClassName.includes('min-w-') && 'min-w-full',
            !dropdownClassName.includes('max-w-') && `max-w-[calc(100vw - 48px)]`,
            align === 'right' ? 'right-0' : 'left-0',
            openUp ? 'bottom-full mb-1' : 'top-full mt-1',
            title ? 'pt-4' : ''
          )}
        >
          {title && (
            <span
              className="px-4 text-md font-medium text-gray-700 text-nowrap"
              dangerouslySetInnerHTML={{ __html: title }}
            ></span>
          )}
          {searchable && (
            <div className="px-3 py-2 border-b border-gray-100">
              <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                className="w-full px-3 py-2 h-9 border border-gray-200 rounded-md text-sm outline-none"
                onChange={e => debouncedSetQuery(e.target.value)}
              />
            </div>
          )}
          {description && <div className="px-4 py-4 text-sm text-gray-500">{description}</div>}
          <div className="py-1 max-h-72 overflow-auto w-full">
            {loading ? (
              <div className="px-4 py-2 text-sm text-gray-500">Đang tải...</div>
            ) : (
              (() => {
                const list = (loadOptions ? asyncOptions || [] : options) || [];
                if (!list.length) {
                  return <div className="px-4 py-2 text-sm text-gray-500">{noOptionsText}</div>;
                }
                return (
                  <>
                    {allOption && (
                      <button
                        key="allOption"
                        className={cn(
                          'w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 cursor-pointer flex items-center gap-2',
                          optionClassName
                        )}
                        onClick={() => handleClear()}
                      >
                        {!multiple && allOption}
                        {multiple && (
                          <Checkbox
                            checked={!value?.length}
                            label={allOption}
                            onChange={() => handleClear()}
                          />
                        )}
                      </button>
                    )}
                    {list.map((option, index) => (
                      <button
                        key={`${option.value}-${index}`}
                        className={cn(
                          'w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 cursor-pointer flex items-center gap-2',
                          optionClassName,
                          option.className,
                          multiple
                            ? (
                                Array.isArray(value)
                                  ? value.includes(
                                      valueFunc ? (valueFunc(option) ?? '') : (option.value ?? '')
                                    )
                                  : value === option.value
                              )
                              ? 'bg-gray-100'
                              : ''
                            : ''
                        )}
                        onClick={() => !multiple && handleOptionClick(option)}
                      >
                        {multiple && (
                          <Checkbox
                            checked={
                              multiple
                                ? Array.isArray(value)
                                  ? value.includes(option.value ?? '')
                                  : value === option.value
                                : false
                            }
                            label={htmlFunc ? htmlFunc(option) : option.label}
                            onChange={() => handleOptionClick(option)}
                          />
                        )}
                        {!multiple &&
                          (htmlFunc ? (
                            htmlFunc(option)
                          ) : (
                            <span className="flex items-center gap-2">
                              {option.icon}
                              {option.label}
                            </span>
                          ))}
                      </button>
                    ))}
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
